import React, { useCallback, useState } from "react";
import ListItem from "../../../ui_atoms/ListItem";
import { BonusInput, SalaryDeductionType } from "../../../__generated__/graphql";
import BonusCreateModal, { CreateBonusFormData } from "./BonusCreateModal";
import { useI18n } from "../../../hooks/i18n-hooks";
import { useTheme } from "../../../hooks/use-theme";
import { Alert } from "../../Alert";
import VStack from "../../../ui_atoms/VStack";
import { TitleMediumText } from "../../FormattedText";
import { formatCurrency } from "../../../utils";
import IconButton from "../../IconButton";

type BonusProps = {
    onCreate: (bonus: BonusInput) => void;
    // onClear: () => void;
    onUpdate: (index: number, bonus: BonusInput) => void;
    onDeleteAtIndex: (index: number) => void;
    disabled?: boolean;
    bonuses?: BonusInput[];
    clientDeductions: SalaryDeductionType[];
};

const Bonuses: React.FC<BonusProps> = ({
    onCreate,
    onUpdate,
    onDeleteAtIndex,
    disabled = false,
    bonuses = [],
    clientDeductions,
}) => {
    const { marginSize, colors } = useTheme();
    const [modalVisible, setModalVisible] = React.useState(false);
    const i18n = useI18n();

    const onActionRemove = useCallback(
        (index: number) => {
            Alert.alert(i18n.t("bonus.remove_title"), i18n.t("bonus.remove_info"), [
                {
                    text: i18n.t("cancel"),
                    style: "cancel",
                },
                {
                    text: i18n.t("bonus.remove_action"),
                    onPress: () => onDeleteAtIndex(index),
                    style: "destructive",
                },
            ]);
        },
        [i18n, onDeleteAtIndex],
    );

    const [createModalEditValues, setCreateModalEditValues] = useState<
        { index: number } & CreateBonusFormData
    >();

    return (
        <>
            <BonusCreateModal
                visible={modalVisible}
                initialValues={createModalEditValues}
                onClose={() => setModalVisible(false)}
                onCreate={(a) => {
                    setModalVisible(false);
                    if (!createModalEditValues) {
                        onCreate(a);
                    } else {
                        onUpdate(createModalEditValues.index, a);
                        setCreateModalEditValues(undefined);
                    }
                }}
                clientDeductions={clientDeductions}
            />
            <VStack gap="1/3">
                <TitleMediumText>{i18n.t("bonus.title")}</TitleMediumText>
                {bonuses.map((bonus, index) => (
                    <ListItem
                        key={index}
                        title={`${i18n.t("bonus.title")} ${formatCurrency({
                            amountInCents: bonus.amount,
                            displayDigitsWhenNoCents: false,
                        })}`}
                        labels={[
                            {
                                text: i18n.t("payroll_breakdown.edit"),
                                mode: disabled ? undefined : "miniCtaBold",
                                onPress: () => {
                                    setCreateModalEditValues({
                                        index,
                                        amount: bonus.amount,
                                        description: bonus.description,
                                    });
                                    setModalVisible(true);
                                },
                            },
                        ]}
                        description={bonus.description}
                        maxDescriptionLines={2}
                        onDelete={() => onDeleteAtIndex(index)}
                        badge={
                            <IconButton
                                onPress={() => onActionRemove(index)}
                                icon="trash"
                                size={20}
                                iconColor={colors.primary}
                                style={{ marginRight: marginSize / 2 }}
                            />
                        }
                        style={{
                            backgroundColor: colors.background,
                        }}
                        swipeableStyle={{
                            marginHorizontal: -marginSize,
                            borderRadius: 0,
                        }}
                    />
                ))}
                <ListItem
                    labels={[
                        {
                            text: i18n.t("bonus.add_title"),
                            mode: disabled ? undefined : "cta",
                        },
                    ]}
                    rightIcon="add"
                    rightActionMode="medium"
                    rightIconColor={colors.secondary}
                    style={{ marginHorizontal: -marginSize }}
                    onPress={() => {
                        if (!disabled) {
                            setCreateModalEditValues(undefined);
                            setModalVisible(true);
                        }
                    }}
                />
            </VStack>
        </>
    );
};

export default Bonuses;
